import {
  CHIP_SETS,
  CHIP_SET_IDS,
  DECKS,
  DECK_IDS,
  FELTS,
  FELT_IDS,
  gameLabel,
  readTheme,
  type ChipSetId,
  type DeckId,
  type FeltId,
} from '@tv-casino/shared';
import type { CasinoLookup } from '@tv-casino/shared';

/**
 * The pure view-model behind the owner console. No React, no fetching — the
 * route feeds it the lookup it already has and renders what comes back.
 *
 * The console is one page with two screens: **tables** (the venue, every table
 * with the TV link for its own screen) and **stats** (the night in chips and
 * the member ledger). There is no venue-wide TV: a screen belongs to a table. Running a live table is the phone's
 * job — the owner's phone carries the full host panel — so nothing here tries
 * to be a dealer's console.
 */

/** The two console screens. */
export type ConsoleView = 'tables' | 'stats';

export interface ConsoleTab {
  id: ConsoleView;
  label: string;
  active: boolean;
}

export interface ConsoleTableRow {
  id: string;
  code: string;
  name: string;
  /** The felt currently on this table — host-picked, not create-time. */
  gameLabel: string;
  live: boolean;
  closed: boolean;
  /** The status word the row prints next to its name. */
  note: string;
  /** The table code shown as the row's primary action. */
  actionLabel: string | null;
  /** The TV URL for this table. */
  tvUrl: string | null;
}

/** One house felt the owner can dress the venue in. */
export interface ConsoleFeltOption {
  id: FeltId;
  label: string;
  active: boolean;
}

/** One house chip set the owner can put on the venue's rails. */
export interface ConsoleChipSetOption {
  id: ChipSetId;
  label: string;
  active: boolean;
}

/** One house deck the owner can deal every table from. */
export interface ConsoleDeckOption {
  id: DeckId;
  label: string;
  active: boolean;
}

export interface CasinoConsoleVM {
  casinoName: string;
  joinCode: string;
  /** True when the owner hid the venue's join code and QRs. */
  isPrivate: boolean;
  /** Every house felt, in catalog order, the venue's own one marked active. */
  felts: ConsoleFeltOption[];
  /** Every house chip set, in catalog order, the venue's own one marked active. */
  chipSets: ConsoleChipSetOption[];
  /** Every house deck, in catalog order, the venue's own one marked active. */
  decks: ConsoleDeckOption[];
  view: ConsoleView;
  tabs: ConsoleTab[];
  tables: ConsoleTableRow[];
  liveTableCount: number;
  /** The one line under the venue name: where the night currently is. */
  nightStatus: string;
  /** The next thing to do, when the night is not running yet. */
  nextStep: string | null;
  emptyTablesLine: string | null;
  /** `/play?casino=CODE` — what a guest's phone opens, by QR or by link. */
  playUrl: string;
}

const TABS = (view: ConsoleView): ConsoleTab[] => [
  { id: 'tables', label: 'Tables', active: view === 'tables' },
  { id: 'stats', label: 'Stats', active: view === 'stats' },
];

/** The empty console: signed in, but no casino picked yet. */
const EMPTY: CasinoConsoleVM = {
  casinoName: '',
  joinCode: '',
  isPrivate: false,
  felts: [],
  chipSets: [],
  decks: [],
  view: 'tables',
  tabs: TABS('tables'),
  tables: [],
  liveTableCount: 0,
  nightStatus: '',
  nextStep: 'Open a casino first.',
  emptyTablesLine: null,
  playUrl: '/play',
};

export function casinoConsoleModel(
  casino: CasinoLookup | null,
  requested: ConsoleView,
): CasinoConsoleVM {
  if (!casino) return EMPTY;

  const row = (t: CasinoLookup['tables'][number]): ConsoleTableRow => {
    const closed = t.status === 'closed';
    return {
      id: t.id,
      code: t.code,
      name: t.name,
      gameLabel: gameLabel(t.gameType),
      live: t.live,
      closed,
      note: closed ? 'Closed' : t.live ? 'Playing' : 'Open',
      actionLabel: closed ? null : t.code,
      tvUrl: closed ? null : `/tv?table=${t.code}`,
    };
  };

  // The stats screen reads the ledger, which exists from the moment the casino
  // does — so both screens are always somewhere to stand.
  const view: ConsoleView = requested === 'stats' ? 'stats' : 'tables';

  const { felt, chips, deck } = readTheme(casino.theme);

  return {
    casinoName: casino.name,
    joinCode: casino.joinCode,
    isPrivate: casino.isPrivate,
    felts: FELT_IDS.map((id) => ({ id, label: FELTS[id].label, active: id === felt })),
    chipSets: CHIP_SET_IDS.map((id) => ({ id, label: CHIP_SETS[id].label, active: id === chips })),
    decks: DECK_IDS.map((id) => ({ id, label: DECKS[id].label, active: id === deck })),
    view,
    tabs: TABS(view),
    tables: casino.tables.map(row),
    liveTableCount: casino.tables.filter((t) => t.live).length,
    nightStatus:
      casino.tables.length === 0
        ? 'No tables yet'
        : casino.tables.some((t) => t.live)
          ? `${casino.tables.filter((t) => t.live).length} playing`
          : 'Tables ready',
    nextStep: casino.tables.length === 0 ? 'Add a table to open the floor.' : null,
    emptyTablesLine:
      casino.tables.length === 0 ? 'No tables yet — add the first one below.' : null,
    playUrl: `/play?casino=${casino.joinCode}`,
  };
}
